import { api } from "../../api";
import { h } from "../shared/safe";
import { pill } from "../shared/blocks";
import { PAGE_KINDS, describeProblem, parseTargets, showValue, type TargetPage, type TargetsView } from "./factsModel";
import { busyButton, card, labelled, notice, problemNotice, select, textInput, webLink, whoText } from "./kit";
import { enc, type Workspace } from "./workspace";

// TARGETS: the money site and the pages CloudPush builds links to. Saved with PUT .../targets, guarded by the revision (ifRevision).
// A URL or alias can belong to one target only (the server says DUPLICATE_URL). Unresolved proposals that name a target are shown apart.

interface Row {
  label: string;
  url: string;
  kind: string;
  service: string;
  location: string;
  aliases: string;
}
const rowOf = (p: TargetPage): Row => ({ label: p.label, url: p.url, kind: p.kind ?? "", service: p.service ?? "", location: p.location ?? "", aliases: p.aliases.join(", ") });

export function targetsCard(ws: Workspace): HTMLElement {
  const body = h("div");
  const out = card("fx-targets", "Targets: money site and target pages", body);
  let t: TargetsView | null = null;
  let loadError: string | null = null;
  let editing = false;
  let money = "";
  let rows: Row[] = [];
  let reason = "";
  let result: HTMLElement | null = null;
  let stale = false;
  const form = h("form", { class: "fx-form", novalidate: true, "aria-label": "Edit targets" });

  async function load() {
    try {
      const v = parseTargets(await api<unknown>(`/v1/clients/${enc(ws.clientId)}/targets`));
      if (!ws.life.alive()) return;
      t = v;
      loadError = v ? null : "The server sent targets this page does not understand.";
    } catch (e) {
      if (!ws.life.alive()) return;
      loadError = describeProblem(e, "Targets could not be loaded").message;
    }
    paint();
  }

  function start() {
    if (!t) return;
    editing = true;
    stale = false;
    result = null;
    money = t.moneySiteUrl ?? "";
    rows = t.pages.map(rowOf);
    reason = "";
    buildForm();
    paint();
    form.querySelector<HTMLElement>("input")?.focus();
  }

  function buildForm() {
    const moneyInput = textInput(money, { name: "moneySiteUrl", type: "text", placeholder: "https://example.com/", onInput: (v) => void (money = v) });
    const list = h("div", { class: "fx-rows" });
    const draw = () => {
      list.replaceChildren(
        ...rows.map((r, i) => {
          const set = (k: keyof Row) => (v: string) => void (r[k] = v);
          return h(
            "fieldset",
            { class: "fx-row", dataset: { targetRow: String(i) } },
            h("legend", null, `Target page ${i + 1}`),
            h("div", { class: "fx-grid" }, labelled("Label", textInput(r.label, { name: "label", onInput: set("label") })), labelled("URL", textInput(r.url, { name: "url", onInput: set("url"), placeholder: "https://example.com/page/" })), labelled("Kind", select([["", "(none)"], ...PAGE_KINDS.map((k): [string, string] => [k, k])], r.kind, { name: "kind", onChange: set("kind") })), labelled("Service (optional)", textInput(r.service, { name: "service", onInput: set("service") })), labelled("Location (optional)", textInput(r.location, { name: "location", onInput: set("location") })), labelled("Aliases (optional, comma-separated URLs)", textInput(r.aliases, { name: "aliases", onInput: set("aliases") }), { wide: true })),
            h("button", { type: "button", class: "secondary", "aria-label": `Remove target page ${i + 1}`, on: { click: () => { rows.splice(i, 1); draw(); } } }, "Remove"),
          );
        }),
      );
    };
    draw();
    const save = busyButton({ label: "Save targets", busyLabel: "Saving…", id: "fx-targets-save", type: "submit", onClick: () => saveIt() });
    form.onsubmit = (e) => {
      e.preventDefault();
      void save.click();
    };
    form.replaceChildren(
      labelled("Money site URL", moneyInput, { help: "The client's main website. Links to exactly this address are counted as the money site." }),
      h("h3", null, "Target pages"),
      list,
      h("button", { type: "button", class: "secondary", id: "fx-targets-add", on: { click: () => { rows.push({ label: "", url: "", kind: "", service: "", location: "", aliases: "" }); draw(); list.querySelector<HTMLElement>(".fx-row:last-child input")?.focus(); } } }, "+ Add a target page"),
      labelled("Reason (optional)", textInput("", { name: "reason", onInput: (v) => void (reason = v) })),
      h("div", { id: "fx-targets-conflict", "aria-live": "polite" }),
      h("div", { class: "btn-group" }, save, h("button", { type: "button", class: "secondary", on: { click: () => { editing = false; paint(); } } }, "Cancel")),
      h("div", { id: "fx-targets-result", "aria-live": "polite" }),
    );
  }

  async function saveIt() {
    if (!t) return;
    const pages = rows.map((r) => ({
      label: r.label.trim(),
      url: r.url.trim(),
      ...(r.kind ? { kind: r.kind } : {}),
      ...(r.service.trim() ? { service: r.service.trim() } : {}),
      ...(r.location.trim() ? { location: r.location.trim() } : {}),
      ...(r.aliases.trim() ? { aliases: r.aliases.split(",").map((a) => a.trim()).filter(Boolean) } : {}),
    }));
    const slot = form.querySelector("#fx-targets-result")!;
    if (pages.some((p) => !p.label || !p.url)) return void slot.replaceChildren(notice("bad", "Every target page needs a label and a URL. Nothing was saved."));
    try {
      const res = await api<{ revision?: number; updated?: boolean }>(`/v1/clients/${enc(ws.clientId)}/targets`, { method: "PUT", body: JSON.stringify({ moneySiteUrl: money.trim() === "" ? null : money.trim(), pages: pages.length ? pages : null, ifRevision: t.revision, reason: reason.trim() || undefined }) });
      if (!ws.life.alive()) return;
      editing = false;
      result = notice("ok", res.updated === false ? "Nothing changed." : `Saved${typeof res.revision === "number" ? `: revision ${res.revision}` : ""}.`);
      await load();
      await ws.wrote();
    } catch (e) {
      if (!ws.life.alive()) return;
      const p = describeProblem(e, "Targets were not saved");
      if (p.kind === "stale") {
        stale = true;
        form.querySelector("#fx-targets-conflict")?.replaceChildren(
          h("div", { class: "fx-conflict", role: "alert", id: "fx-targets-stale" }, h("strong", null, "Conflict, reload"), h("p", null, p.message), busyButton({ label: "Reload targets (discard my edits)", busyLabel: "Reloading…", id: "fx-targets-reload", onClick: async () => { await load(); if (ws.life.alive()) { await ws.refreshFacts(); editing = false; paint(); } } })),
        );
        form.querySelector<HTMLElement>("#fx-targets-reload")?.focus();
        return;
      }
      slot.replaceChildren(problemNotice(p));
    }
  }

  function readView(v: TargetsView): HTMLElement {
    return h(
      "div",
      null,
      h("div", { class: "fx-bar" }, pill(`Revision ${v.revision}`, "neutral"), h("button", { type: "button", id: "fx-targets-edit", on: { click: start } }, "Edit targets")),
      ...(v.parseIssue ? [notice("warn", "Stored target pages could not be read", [v.parseIssue])] : []),
      h("dl", { class: "deflist fx-deflist" }, h("dt", null, "Money site"), h("dd", { dataset: { target: "money" } }, webLink(v.moneySiteUrl))),
      h("h3", null, `Target pages (${v.pages.length})`),
      v.pages.length === 0
        ? h("p", { class: "muted" }, "No target pages recorded. Links to pages other than the money site are listed as other targets in the created links report.")
        : h("ul", { class: "fx-targets-list" }, ...v.pages.map((p) => h("li", { dataset: { targetUrl: p.url } }, h("strong", null, p.label), " ", webLink(p.url), p.kind ? h("span", { class: "tag" }, p.kind) : null, p.service ? h("span", { class: "muted" }, ` · service: ${p.service}`) : null, p.location ? h("span", { class: "muted" }, ` · location: ${p.location}`) : null, p.aliases.length ? h("div", { class: "muted fx-small" }, `Aliases: ${p.aliases.join(", ")}`) : null))),
      v.gbpLinks.length
        ? h("div", null, h("h3", null, "GBP link targets (reference data)"), h("ul", null, ...v.gbpLinks.map((g) => h("li", null, `${g.label} (${g.status}): `, webLink(g.mapsUrl), g.publicUrl ? [" · ", webLink(g.publicUrl)] : null))))
        : null,
      v.proposals.length
        ? h("div", { class: "fx-unr-banner", role: "note" }, h("strong", null, "UNRESOLVED, not approved: "), "proposed target information waiting on the Unresolved information card: ", v.proposals.map((p) => showValue(p.proposedValue, 80)).join("; "))
        : null,
    );
  }

  function paint() {
    if (!t) return void body.replaceChildren(loadError ? h("div", { class: "error-banner", role: "alert" }, loadError, " ", h("button", { type: "button", class: "secondary", on: { click: () => void load() } }, "Try again")) : h("p", { class: "muted" }, "Loading…"));
    body.replaceChildren(...(result && !editing ? [result] : []), editing ? form : readView(t));
  }

  ws.onWrite(() => {
    if (!editing) void load();
  });
  void load();
  paint();
  void whoText;
  return out;
}
